/**
 * SwapResourceDialog — T-29 / T-31 guarded team change.
 *
 * Replaces the holder of ONE role on a project via `POST /api/projects/{id}/resources/swap`
 * (Provisioning RestApi — see `useSwapResource`). One server transaction removes the outgoing
 * person from that role only, assigns the replacement, writes an audit row, and notifies both
 * people in the project channel — so the dialog asks for a reason and says so up front.
 *
 * The replacement picker is the shared `ResourcePicker` with its default delivery pool (T-09 —
 * the SSM-configured delivery pool). Named-lead roles relax the
 * `submits_timesheets` filter exactly like the Add dialog (`requireAssignable`).
 *
 * Accessibility: Radix Dialog provides the focus trap, Esc-to-close and focus return. The reason
 * field is labelled, its counter and error are tied to it with `aria-describedby`, the error is a
 * `role="alert"` text message (never colour alone), and `aria-invalid` flags the field.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import type { AxiosError } from "axios";

import {
  SWAP_REASON_MAX,
  SWAP_REASON_MIN,
  useSwapResource,
} from "@/hooks/useResources";
import { isNamedLeadRole, projectRoleLabel } from "@/types/intake";
import { ResourcePicker } from "@/components/resources/ResourcePicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface SwapTarget {
  /** Outgoing person's `resources.id`. */
  resourceId: string;
  /** Outgoing person's display name. */
  name: string;
  /** Stored `role_on_project` wire value (posted unchanged). */
  role: string;
}

export interface SwapResourceDialogProps {
  projectId: string;
  target: SwapTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful swap (the Team query is already invalidated). */
  onSwapped?: (replacementName: string) => void;
}

function serverMessage(err: unknown): string {
  const axiosErr = err as AxiosError<{ message?: string }>;
  return (
    axiosErr?.response?.data?.message ??
    (err instanceof Error ? err.message : "Failed to swap team member")
  );
}

export function SwapResourceDialog({
  projectId,
  target,
  open,
  onOpenChange,
  onSwapped,
}: SwapResourceDialogProps): JSX.Element {
  const swap = useSwapResource(projectId);
  const [replacementId, setReplacementId] = useState("");
  const [replacementName, setReplacementName] = useState("");
  const [reason, setReason] = useState("");
  const [pickerError, setPickerError] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [formError, setFormError] = useState("");

  const roleLabel = projectRoleLabel(target.role);
  const trimmedLength = reason.trim().length;

  function handlePick(id: string, name: string): void {
    setReplacementId(id);
    setReplacementName(name);
    setPickerError("");
    setFormError("");
  }

  function handleReason(value: string): void {
    setReason(value);
    setReasonError("");
    setFormError("");
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    let invalid = false;
    if (!replacementId) {
      setPickerError("Select a replacement.");
      invalid = true;
    } else if (replacementId === target.resourceId) {
      setPickerError(`Choose someone other than ${target.name}.`);
      invalid = true;
    }
    if (trimmedLength < SWAP_REASON_MIN) {
      setReasonError(`Reason must be at least ${SWAP_REASON_MIN} characters.`);
      invalid = true;
    }
    if (invalid) return;

    setFormError("");
    swap.mutate(
      {
        role_on_project: target.role,
        from_resource_id: target.resourceId,
        to_resource_id: replacementId,
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          onSwapped?.(replacementName);
          onOpenChange(false);
        },
        onError: (err) => setFormError(serverMessage(err)),
      },
    );
  }

  const describedBy = [
    "swap-reason-counter",
    reasonError && "swap-reason-error",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{`Swap ${roleLabel}`}</DialogTitle>
          <DialogDescription>
            {`Replace ${target.name} as ${roleLabel}. Both people are notified in the project channel, and the reason is recorded.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-text-secondary">
              Current {roleLabel}
            </p>
            <p
              className="text-sm font-medium text-text-primary"
              data-testid="swap-current-person"
            >
              {target.name}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="swap-resource-picker">Replacement</Label>
            <ResourcePicker
              id="swap-resource-picker"
              value={replacementId}
              onChange={handlePick}
              placeholder="Search by name or email…"
              requireAssignable={!isNamedLeadRole(target.role)}
              // 2026-10-22: only people whose title fits the role being swapped (server-side).
              roleOnProject={target.role}
            />
            {pickerError && (
              <p className="text-sm text-error" role="alert">
                {pickerError}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="swap-reason">
              Reason <span aria-hidden="true">*</span>
            </Label>
            <Textarea
              id="swap-reason"
              value={reason}
              onChange={(e) => handleReason(e.target.value)}
              maxLength={SWAP_REASON_MAX}
              required
              aria-required="true"
              aria-invalid={reasonError ? true : undefined}
              aria-describedby={describedBy}
              placeholder="Why is this person being replaced?"
            />
            <p id="swap-reason-counter" className="text-xs text-text-secondary">
              {`${trimmedLength}/${SWAP_REASON_MAX} characters (minimum ${SWAP_REASON_MIN})`}
            </p>
            {reasonError && (
              <p
                id="swap-reason-error"
                className="text-sm text-error"
                role="alert"
              >
                {reasonError}
              </p>
            )}
          </div>

          {formError && (
            <p id="swap-form-error" className="text-sm text-error" role="alert">
              {formError}
            </p>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={swap.isPending}>
              {swap.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Confirm swap
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default SwapResourceDialog;
